import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';
import { classes } from 'typestyle';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { NumberInputRange } from '~src/settings/general-settings/utils';

import { Messages } from '../../../../../codegen/Localize';
import { fetch,ILicensingInfoWithTotals } from '../../../../../userManagement/services/licenses/LicensesService';
import { disabledText, settingsRadioButtonItemHeight } from '../../../../styles/CommonStyles';
import { useSharedWidgetChangeHandler } from '../../../utils';
import { ManageUsersSectionProps } from './ManageUsersSection';
import { ManageUsersServerLimitOption } from './ManageUsersServerLimitOption';
import { ManageUsersSiteRoleQuotaOption } from './ManageUsersSiteRoleQuotaOption';
import { ManageUsersSiteUserQuotaOption } from './ManageUsersSiteUserQuotaOption';

export const USER_QUOTA_MODE_FIELD = 'settings.userQuotaMode';
export function ManageUsersAdminOption(props: ManageUsersSectionProps) {
  const formik = useFormikContext<ManageUsersSectionProps>();
  const disabled = ! formik.values.settings.siteAdminsCanAddUsers;

  const licenseMaxCounts = useLicensingInfoInfo();

  const buttons = React.useMemo(() => getButtonsList({
    ...props,
    maxCounts: licenseMaxCounts
  }, disabled), [props, licenseMaxCounts, disabled]);

  const index = buttons.findIndex(button => button.value === formik.values.settings.userQuotaMode);

  const handleChange = useSharedWidgetChangeHandler<string>(USER_QUOTA_MODE_FIELD);
  return <>
    <div className={classes({ [disabledText]: disabled })}>{Messages.usersDetails()}</div>
    <RadioButtonWithLabelGroupWidget buttonList={buttons}
                                     aria-label={Messages.managingUsersDetails()}
                                     className={settingsRadioButtonItemHeight}
                                     removeLeftMargin
                                     checkedIndex={index}
                                     handleChange={handleChange}/>
  </>;
}

function getButtonsList(props: ManageUsersSectionProps & { maxCounts: LicenseMaxCounts }, disabled: boolean): RadioButtonWithLabelOption<string>[] {
  const buttons: RadioButtonWithLabelOption<string>[] = [
    {
      value: 'server-limit',
      label: Messages.serverLimit(),
      detail: <ManageUsersServerLimitOption {...props} />,
      testId: 'user-quota-unlimited-radio-button',
      disabled
    },
    {
      value: 'site-user-quota',
      label: Messages.siteLimit(),
      detail: <ManageUsersSiteUserQuotaOption {...props} />,
      testId: 'user-quota-enabled-radio-button',
      disabled
    }
  ];

  if(FeatureFlags.siteQuotaEnabled) {
    buttons.push({
      value: 'site-tiered-capacity',
      label: Messages.siteRoleLimit(),
      detail: <ManageUsersSiteRoleQuotaOption {...props} />,
      testId: 'site-tiered-quota-enabled-radio-button',
      disabled
    });
  }

  return buttons;
}

export interface LicenseMaxCounts {
  userCountMax: number;
  tierCreatorCountMax: number;
  tierExplorerCountMax: number;
  tierViewerCountMax: number;
}

export function useLicensingInfoInfo() {
  const [licenseMaxCounts, setLicenseMaxCounts] = React.useState<LicenseMaxCounts>(null);

  React.useEffect(() => {
    if (!licenseMaxCounts) {
      fetch().then((licensingInfo: ILicensingInfoWithTotals) => {
        if (licensingInfo) {
          const counts = calculateUserMaxCounts(licensingInfo);
          setLicenseMaxCounts(counts);
        }
      });
    }
  }, [licenseMaxCounts]);

  return licenseMaxCounts;
}

export function calculateUserMaxCounts(licensingInfo: ILicensingInfoWithTotals): LicenseMaxCounts {
  let tierCreatorCountMax = licensingInfo.totalCreatorUsers ?? 0;
  let tierExplorerCountMax = licensingInfo.totalExplorerUsers ?? 0;
  let tierViewerCountMax = licensingInfo.totalViewerUsers ?? 0;
  let userCountMax = tierCreatorCountMax + tierExplorerCountMax + tierViewerCountMax;

  const noLicenses = !licensingInfo.productKeyInfos || !licensingInfo.productKeyInfos.length;
  if (licensingInfo.coreLicensingInfos || noLicenses) {
    tierExplorerCountMax = NumberInputRange.INT.MAX;
    tierViewerCountMax = NumberInputRange.INT.MAX;
    userCountMax = NumberInputRange.INT.MAX;
  }

  return {
    userCountMax,
    tierCreatorCountMax,
    tierExplorerCountMax,
    tierViewerCountMax
  };
}
